import React, { useContext, useEffect, useMemo, useState } from "react";
import FilterCard from "./FilterCard";
import { CogIcon } from "@heroicons/react/24/outline";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/Graphs/Popover";
import { Checkbox } from "components/Graphs/Checkbox";
import { OrgContext } from "contexts/OrgContext";

interface CapabilityFilterProps {
  persistentOnly: boolean;
  setPersistentOnly: (persistentOnly: boolean) => void;
  flexiblePortControlOnly: boolean;
  setFlexiblePortControlOnly: (flexiblePortControlOnly: boolean) => void;
}

const CapabilityFilter: React.FC<CapabilityFilterProps> = ({
  persistentOnly,
  setPersistentOnly,
  flexiblePortControlOnly,
  setFlexiblePortControlOnly,
}) => {
  const getDisplayText = () => {
    if (!persistentOnly && !flexiblePortControlOnly) {
      return "Any";
    }

    const filters: string[] = [];
    if (persistentOnly) filters.push("Persistent");
    if (flexiblePortControlOnly) filters.push("Flexible Ports");

    return "(" + filters.length + ")";
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <div className="mr-2 ml-2 flex">
          <FilterCard
            icon={<CogIcon className="w-6 h-6 text-white mr-1" />}
            title="Instance Capabilities"
            description={getDisplayText()}
            descriptionClass="mt-[3px]"
            className="min-w-[200px]"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent className="p-4">
        <div className="flex flex-col min-w-[400px]">
          <div className="flex flex-col text-sm font-medium text-white mb-3">
            <h1>Filter Instances by Capability</h1>
            <p className="text-xs text-secondary">
              Different cloud providers offer instances with a variety of
              capabilities.
            </p>
          </div>

          {/* Instance Type Capabilities */}
          <div className="flex flex-col mb-4">
            <h2 className="text-sm font-medium text-white mb-2">
              Instance Lifecycle
            </h2>
            <div className="flex flex-col space-y-2">
              <div className={`flex items-start space-x-3 p-2 rounded-md`}>
                <Checkbox
                  checked={persistentOnly}
                  onCheckedChange={setPersistentOnly}
                  id="persistent-only"
                />
                <div className="flex flex-col">
                  <label
                    htmlFor="persistent-only"
                    className="text-sm font-medium cursor-pointer text-secondary"
                  >
                    Only Show Persistent Instances
                  </label>
                  <p className="text-xs text-secondary mt-1">
                    Instances that can be stopped and started with data
                    persistence
                  </p>
                </div>
              </div>
            </div>
          </div>

          <hr className="w-[100%] mb-3 dark:border dark:border-zinc-800" />

          {/* Port Control Capabilities */}
          <div className="flex flex-col">
            <h2 className="text-sm font-medium text-white mb-2">
              Port Control
            </h2>
            <div className="flex flex-col space-y-2">
              <div className={`flex items-start space-x-3 p-2 rounded-md`}>
                <Checkbox
                  checked={flexiblePortControlOnly}
                  onCheckedChange={setFlexiblePortControlOnly}
                  id="flexible-port-control-only"
                />
                <div className="flex flex-col">
                  <label
                    htmlFor="flexible-port-control-only"
                    className="text-sm font-medium cursor-pointer text-secondary"
                  >
                    Only Show Flexible Port Control Instances
                  </label>
                  <p className="text-xs text-secondary mt-1">
                    Configure custom firewall rules and open/close ports anytime
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default CapabilityFilter;
